'use client';

import React from 'react';

/**
 * Hook that alerts clicks outside of the passed ref
 */
export const useClickOutside = (
  ref: React.MutableRefObject<HTMLElement | null>,
  callback: (event?: MouseEvent) => void,
  boundaryRef?: React.RefObject<HTMLElement | null>,
): void => {
  React.useEffect(() => {
    /**
     * Alert if clicked on outside of element
     */
    function handleClickOutside(event: MouseEvent) {
      const target = event.target as Node | null;
      if (!ref?.current || !target) return;
      if (ref.current.contains(target)) return;
      if (boundaryRef?.current?.contains(target)) return;
      callback(event);
    }

    // Bind the event listener
    document.addEventListener('mousedown', handleClickOutside);
    window.addEventListener(
      'blur',
      () => {
        setTimeout(() => {
          if (document.activeElement?.tagName === 'IFRAME') {
            callback();
          }
        });
      },
      { once: true },
    );
    return () => {
      // Unbind the event listener on clean up
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [boundaryRef, callback, ref]);
};
